import { filterCampaignHoles } from './drillingMetricsForReport';
import { resolveSourceProjectName } from '../drillingCampaignHelpers';

/** Color palette for zones (prospects) — shared with Drilling Dashboard */
export const ZONE_COLORS = [
  '#f97316',
  '#14b8a6',
  '#a855f7',
  '#ec4899',
  '#eab308',
  '#22c55e',
  '#3b82f6',
  '#ef4444',
  '#06b6d4',
  '#8b5cf6',
  '#f43f5e',
  '#84cc16',
];

const ACTIVE_STATUSES = new Set(['Complete', 'In Progress', 'Lost']);

function getHoleDrilledDepth(hole) {
  if (hole.hole_status === 'In Progress') {
    return parseFloat(hole.current_depth) || parseFloat(hole.final_depth) || 0;
  }
  return parseFloat(hole.final_depth) || 0;
}

function hasValidCoords(hole) {
  const lat = parseFloat(hole.latitude);
  const lon = parseFloat(hole.longitude);
  return Number.isFinite(lat) && Number.isFinite(lon) && lat !== 0 && lon !== 0;
}

export function buildZoneColorMap(holes) {
  const uniqueZones = [...new Set(holes.map((h) => h.prospect).filter(Boolean))].sort();
  const zoneColorMap = {};
  uniqueZones.forEach((zone, i) => {
    zoneColorMap[zone] = ZONE_COLORS[i % ZONE_COLORS.length];
  });
  return zoneColorMap;
}

export function buildAllZoneFilters(zoneColorMap) {
  return Object.fromEntries(Object.keys(zoneColorMap).map((zone) => [zone, true]));
}

export function buildZoneLegendEntries(holes, zoneColorMap) {
  const stats = {};

  holes.forEach((hole) => {
    if (hole.hole_status === 'Planned') return;
    const zone = hole.prospect || 'Unknown';
    if (!stats[zone]) {
      stats[zone] = { count: 0, totalDepthM: 0 };
    }
    stats[zone].count += 1;
    stats[zone].totalDepthM += getHoleDrilledDepth(hole);
  });

  return Object.entries(zoneColorMap)
    .map(([zone, color]) => {
      const row = stats[zone] || { count: 0, totalDepthM: 0 };
      return {
        zone,
        color,
        count: row.count,
        totalDepthM: Math.round(row.totalDepthM),
      };
    })
    .sort((a, b) => b.totalDepthM - a.totalDepthM);
}

export function buildMapHolesForReport(dashboardData, selectedProjectId, options = {}) {
  const { inProgressOnly = false } = options;
  const campaignHoles = filterCampaignHoles(dashboardData, selectedProjectId);
  let holes = campaignHoles.filter(
    (hole) => ACTIVE_STATUSES.has(hole.hole_status) && hasValidCoords(hole)
  );

  if (inProgressOnly) {
    const inProgressHoles = holes.filter((hole) => hole.hole_status === 'In Progress');
    if (inProgressHoles.length > 0) {
      holes = inProgressHoles;
    }
  }

  return holes;
}

export function buildDrillingMapReportContext({
  dashboardData,
  selectedProjectId,
  projectSources,
  sampleProgressBundle,
  frameInProgressOnly = true,
}) {
  const holes = buildMapHolesForReport(dashboardData, selectedProjectId);
  let exportBoundsHoles = holes;

  if (frameInProgressOnly) {
    const inProgressHoles = holes.filter((hole) => hole.hole_status === 'In Progress');
    if (inProgressHoles.length > 0) {
      exportBoundsHoles = inProgressHoles;
    }
  }

  const zoneColorMap = buildZoneColorMap(holes);
  const zoneLegend = buildZoneLegendEntries(holes, zoneColorMap);
  const zoneFilters = buildAllZoneFilters(zoneColorMap);

  const campaignLabel =
    selectedProjectId === 'all'
      ? 'All projects'
      : resolveSourceProjectName(selectedProjectId, sampleProgressBundle?.projects) ||
        String(selectedProjectId);

  return {
    holes,
    exportBoundsHoles,
    zoneColorMap,
    zoneLegend,
    zoneFilters,
    campaignLabel,
    hasMapData: holes.length > 0,
  };
}
